// The measurement contract (#24), shared by the app and the API so the two can
// never disagree on a name. Read docs/memory/reference/telemetry-rollup-design.md
// before adding anything: few events, numbers sent as numbers.

/** Server counts, written to Analytics Engine by the API and the poll rooms. */
export const SERVER_EVENTS = [
  "poll_created",
  "vote_cast",
  "vote_changed",
  "poll_closed",
  "poll_expired",
  "poll_reported",
  "account_created",
  "purchase_completed",
  "funnel_forwarded",
  /** A poll was published from a template page. label = the template id ("classroom/exit-ticket"). */
  "poll_from_template",
  /** A poll reached 5 voters, and 10 voters: once per poll, counted by its room. */
  "poll_reached_5_votes",
  "poll_reached_10_votes",
  /** A device published its second poll (from the device's own poll list). */
  "creator_second_poll",
  /** A device published a poll 1 to 7 days after its previous one. label = the days. */
  "creator_returned_7d",
  /** The creator opened from a vote page's link (?via=vote), and a poll published from there. */
  "vote_to_creator_view",
  "vote_to_creator_published",
  /** A Pro control was used. label = the setting ("theme:ember", "schedule", "password", ...). */
  "pro_setting_changed",
  /** A poll with more than one question was published. label = its number of questions. */
  "question_set_created",
  /** The host moved a question set to another question (Next, Previous or a jump). */
  "question_set_advanced",
  /** A voter shared their vote from the vote page (Nav, 2026-10-30). label = how: "native", "whatsapp" or "copy". Never the poll or the pick. */
  "vote_shared",
  /**
   * Where a new host came from (viral growth plan, 2026-11-03), on their first poll only and never
   * under GPC or DNT. label = the referrer host ("line.me") or "direct", and the landing path.
   */
  "host_source_ref",
  "host_source_land",
  /** A page a person opened, counted first-party by type (label), never by poll code (Nav, 2026-10-12). */
  "page_view",
  "health_check",
] as const;
export type ServerEvent = (typeof SERVER_EVENTS)[number];

/**
 * The page types a view is counted as: the app's routes plus the marketing site. A poll's
 * pages are counted as their kind (vote, present, remote), so no code ever leaves the page.
 */
export const PAGE_TYPES = ["site", "create", "vote", "present", "remote", "my", "join", "signin", "signin-confirm", "upgrade", "checkout", "checkout-success", "help", "admin", "notfound"] as const;
export type PageType = (typeof PAGE_TYPES)[number];

/**
 * The product funnel, sent cookieless to PostHog through our own /api/e
 * (no SDK, no cookies, never from the vote page). About five per visit.
 */
export const FUNNEL_EVENTS = {
  /** The creator was shown. `site`: 1 when embedded on a marketing page; `vote`: 1 when opened from a vote page. */
  creator_viewed: ["site", "vote"],
  /** A poll went live. Counts, not content: never the question or answers. */
  poll_published: ["answers", "multi", "site", "questions"],
  /** The presenter saw its first vote. `seconds` from publish to that vote. */
  first_vote_seen: ["seconds"],
  /** A sign-in finished. `claimed`: polls moved into the account. */
  signin_completed: ["claimed"],
  /** The contest offer on the host's remote (Nav, 2026-10-30): `shown`, `clicked` or `dismissed` set to 1. */
  contest_offer: ["shown", "clicked", "dismissed"],
  /** A purchase finished. `plan` is one of the three price ids' names. */
  checkout_completed: ["plan"],
} as const satisfies Record<string, readonly string[]>;
export type FunnelEvent = keyof typeof FUNNEL_EVENTS;
export type FunnelProps<E extends FunnelEvent> = Partial<Record<(typeof FUNNEL_EVENTS)[E][number], number | string>>;

export const FUNNEL_PLANS = ["pro_monthly", "pro_yearly", "event_pass"] as const;

/** Validates a funnel event body from a client. Returns the clean event or null. */
export function parseFunnelEvent(body: unknown): { e: FunnelEvent; p: Record<string, number | string>; d: string } | null {
  if (!body || typeof body !== "object") return null;
  const { e, p, d } = body as { e?: unknown; p?: unknown; d?: unknown };
  if (typeof e !== "string" || !Object.hasOwn(FUNNEL_EVENTS, e)) return null;
  if (typeof d !== "string" || !/^[a-z0-9]{16,40}$/.test(d)) return null;
  const allowed = FUNNEL_EVENTS[e as FunnelEvent] as readonly string[];
  const props: Record<string, number | string> = {};
  if (p && typeof p === "object") {
    for (const [k, v] of Object.entries(p as Record<string, unknown>)) {
      if (!allowed.includes(k)) return null;
      if (typeof v === "number" && Number.isFinite(v)) props[k] = v;
      else if (k === "plan" && typeof v === "string" && (FUNNEL_PLANS as readonly string[]).includes(v)) props[k] = v;
      else return null;
    }
  }
  return { e: e as FunnelEvent, p: props, d };
}

/** Monthly quotas the daily usage check warns about at 80%. */
export const USAGE_LIMITS = [
  { event: "funnel_forwarded", monthly: 1_000_000, label: "PostHog free plan (1M events a month; overage is dropped, never billed)" },
  { event: "vote_cast", monthly: 10_000_000, label: "Analytics Engine data points included with Workers Paid (10M a month)" },
] as const satisfies readonly { event: ServerEvent; monthly: number; label: string }[];
export const USAGE_ALERT_AT = 0.8;
